import api from "./axios";
import authAxios from "./authAxios";

// ============ Authentication APIs (uses main backend) ============
export const authAPI = {
  // OTP-based authentication (phone)
  sendOTP: (phone) => api.post("/api/auth/send-otp/", { phone }),
  verifyOTP: (phone, code, role) => api.post("/api/auth/verify-otp/", { phone, code, role }),
  resetPassword: (data) => api.post("/api/auth/reset-password/", data),

  // Legacy login methods
  login: (credentials) => api.post("/api/auth/login/", credentials), // credentials include role if added
  demoLogin: (role) => api.post("/api/auth/demo-login/", { role }, { withCredentials: false }),
  register: (userData) => api.post("/api/auth/register", userData),
  logout: () => api.post("/api/auth/logout/"),
  refreshToken: () => api.post("/api/auth/refresh/"),
  getCurrentUser: () => api.get("/api/auth/user/"),
  lookupUser: (data) => api.post("/api/auth/lookup/", data),
  getActiveRoles: () => api.get("/api/users/roles/active/"),
};

// ============ Product/Inventory APIs ============
export const productAPI = {
  // Product endpoints
  getAllProducts: (params = {}) => api.get("/api/product/products/", { params }),
  getProduct: (id) => api.get(`/api/product/products/${id}/`),
  createProduct: (productData) => api.post("/api/product/products/", productData),
  updateProduct: (id, productData) => api.patch(`/api/product/products/${id}/`, productData),
  deleteProduct: (id) => api.delete(`/api/product/products/${id}/`),
  searchProducts: (query) => api.get(`/api/product/products/?search=${query}`),
  checkStockAlerts: () => api.post("/api/product/check-alerts/"),
  getTaxRates: () => api.get("/api/common/taxes/"),
  getUnits: () => api.get("/api/common/units/"),

  // Smart HSN endpoints
  searchHSN: (params = {}) => api.get("/api/product/hsn/search/", { params }),
  suggestHSNByCategory: (params = {}) => api.get("/api/product/hsn/suggest/", { params }),
  getHSNMapping: (params = {}) => api.get("/api/product/hsn/mapping/", { params }),
  saveHSNMapping: (data) => api.post("/api/product/hsn/mapping/", data),
};

// ============ Supplier APIs ============
export const supplierAPI = {
  getAllSuppliers: (params = {}) => api.get("/api/purchase/suppliers/", { params }),
  getSupplier: (id) => api.get(`/api/purchase/suppliers/${id}/`),
  createSupplier: (data) => api.post("/api/purchase/suppliers/", data),
  updateSupplier: (id, data) => api.patch(`/api/purchase/suppliers/${id}/`, data),
  deleteSupplier: (id) => api.delete(`/api/purchase/suppliers/${id}/`),
};

export const categoryAPI = {
  getAllCategories: () => api.get("/api/product/categories/"),
  createCategory: (data) => api.post("/api/product/categories/", data),
  updateCategory: (id, data) => api.patch(`/api/product/categories/${id}/`, data),
  deleteCategory: (id) => api.delete(`/api/product/categories/${id}/`),
};

// ============ Inventory/Stock APIs ============
export const inventoryAPI = {
  // Batch endpoints
  getBatches: (params = {}) => api.get("/api/inventory/batches/", { params }),
  getBatch: (id) => api.get(`/api/inventory/batches/${id}/`),
  createBatch: (batchData) => api.post("/api/inventory/batches/", batchData),
  updateBatch: (id, batchData) => api.patch(`/api/inventory/batches/${id}/`, batchData),
  deleteBatch: (id) => api.delete(`/api/inventory/batches/${id}/`),

  // Movement endpoints (stock in/out)
  getMovements: (params = {}) => api.get("/api/inventory/movements/", { params }),
  createMovement: (movementData) => api.post("/api/inventory/movements/", movementData),

  // Stock adjustment
  adjustStock: (adjustmentData) => api.post("/api/inventory/adjust-stock/", adjustmentData),

  // Stock sync and audit
  syncStock: () => api.post("/api/inventory/sync-stock/"),
  getAuditLogs: (params = {}) => api.get("/api/inventory/audit-logs/", { params }),

  // Expired batches
  getExpiredBatches: () => api.get("/api/inventory/expired-batches/"),

  // Low stock alerts
  getLowStockItems: () => api.get("/api/inventory/low-stock/"),
};

// ============ Customer APIs ============
export const customerAPI = {
  getAllCustomers: () => api.get("/api/customers/"),
  getCustomer: (id) => api.get(`/api/customers/${id}/`),
  createCustomer: (customerData) => api.post("/api/customers/", customerData),
  updateCustomer: (id, customerData) => api.put(`/api/customers/${id}/`, customerData),
  deleteCustomer: (id) => api.delete(`/api/customers/${id}/`),
  searchCustomers: (query) => api.get(`/api/customers/search/?q=${query}`),
};

// ============ Sales/Invoice APIs ============
export const salesAPI = {
  getAllSales: () => api.get("/api/billing/invoices/"),
  getSale: (id) => api.get(`/api/billing/invoices/${id}/`),
  createSale: (saleData) => api.post("/api/billing/invoices/", saleData),
  updateSale: (id, saleData) => api.put(`/api/billing/invoices/${id}/`, saleData),
  deleteSale: (id) => api.delete(`/api/billing/invoices/${id}/`),
  getSalesByDate: (startDate, endDate) =>
    api.get(`/api/billing/invoices/?start=${startDate}&end=${endDate}`),
  getSalesByDateRange: (startDate, endDate) =>
    api.get(`/api/billing/invoices/?start=${startDate}&end=${endDate}`),
};

export const invoiceAPI = salesAPI; // Alias for Django naming

// ============ Purchase APIs ============
export const purchaseAPI = {
  getAllPurchases: () => api.get("/api/purchase/orders/"),
  getPurchase: (id) => api.get(`/api/purchase/orders/${id}/`),
  createPurchase: (purchaseData) => api.post("/api/purchase/orders/", purchaseData),
  updatePurchase: (id, purchaseData) => api.patch(`/api/purchase/orders/${id}/`, purchaseData),
  deletePurchase: (id) => api.delete(`/api/purchase/orders/${id}/`),
  // Direct Stock Inward
  directStockInward: (data) => api.post("/api/purchase/direct-inward/", data),
  // Notify Supplier (Manual)
  notifySupplier: (data) => api.post("/api/purchase/notify-supplier/", data),
};

// ============ Reports APIs ============
export const reportAPI = {
  getSalesReport: (period) => api.get(`/api/reports/sales/?period=${period || 'month'}`),
  getInventoryReport: () => api.get("/api/reports/inventory/"),
  getCustomerReport: () => api.get("/api/reports/customers/"),
  getProfitLossReport: (period) => api.get(`/api/reports/profit-loss/?period=${period || 'month'}`),
  getTaxReport: () => api.get("/api/reports/tax/"),
  exportReport: (type, format) =>
    api.get(`/api/reports/${type}/export/?format=${format}`, { responseType: "blob" }),
};

// ============ Payments API ============
export const paymentAPI = {
  getAllPayments: () => api.get("/api/payments/"),
  getPayment: (id) => api.get(`/api/payments/${id}/`),
  createPayment: (paymentData) => api.post("/api/payments/", paymentData),
  updatePayment: (id, paymentData) => api.put(`/api/payments/${id}/`, paymentData),
  deletePayment: (id) => api.delete(`/api/payments/${id}/`),
};

// ============ Dashboard/Analytics APIs ============
export const dashboardAPI = {
  getOverview: (storeId) => api.get(`/api/dashboard/overview/${storeId ? `?store_id=${storeId}` : ''}`),
  getAnalytics: (period, storeId) => {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (storeId) params.append('store_id', storeId);
    return api.get(`/api/dashboard/analytics/${params.toString() ? `?${params}` : ''}`);
  },
  getSalesAnalytics: (period, storeId) => {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (storeId) params.append('store_id', storeId);
    return api.get(`/api/dashboard/sales/${params.toString() ? `?${params}` : ''}`);
  },
  getTopProducts: (limit = 10, storeId) => api.get(`/api/dashboard/top-products/?limit=${limit}${storeId ? `&store_id=${storeId}` : ''}`),
  getRecentTransactions: (limit = 10, storeId) => api.get(`/api/dashboard/recent/?limit=${limit}${storeId ? `&store_id=${storeId}` : ''}`),
  getSalesExecutivePerformance: (storeId) => api.get(`/api/dashboard/sales-performance/${storeId ? `?store_id=${storeId}` : ''}`),
};

// ============ Store Management APIs ============
export const storeAPI = {
  getAll: () => api.get("/api/common/stores/"),
  getOne: (id) => api.get(`/api/common/stores/${id}/`),
  create: (data) => api.post("/api/common/stores/", data),
  update: (id, data) => api.patch(`/api/common/stores/${id}/`, data),
  delete: (id) => api.delete(`/api/common/stores/${id}/`),
  bulkApplyUpiQr: (data) => api.post("/api/common/stores/bulk-apply-upi/", data),
};


// ============ User/Settings APIs ============
export const userAPI = {
  getProfile: () => api.get("/api/users/profile"),
  updateProfile: (profileData) => api.put("/api/users/profile", profileData),
  changePassword: (passwordData) => api.put("/api/users/change-password", passwordData),
  getSettings: () => api.get("/api/users/settings"),
  updateSettings: (settings) => api.put("/api/users/settings", settings),
};

export const staffAPI = {
  getAllStaff: () => api.get("/api/users/staff/"),
  getStaff: (id) => api.get(`/api/users/staff/${id}/`),
  getStaffPerformance: (storeId) => api.get(`/api/users/staff/performance/${storeId ? `?store_id=${storeId}` : ''}`),
  createStaff: (staffData) => api.post("/api/users/staff/", staffData),
  updateStaff: (id, staffData) => api.patch(`/api/users/staff/${id}/`, staffData),
  deleteStaff: (id) => api.delete(`/api/users/staff/${id}/`),
};

// ============ Employee Workforce / HRM APIs ============
export const employeeWorkforceAPI = {
  getDashboard: () => api.get("/api/employees/dashboard/"),
  checkIn: (data) => api.post("/api/employees/attendance/check-in/", data),
  checkOut: (data) => api.post("/api/employees/attendance/check-out/", data),
  getTodayAttendanceStatus: () => api.get("/api/employees/attendance/today-status/"),
  getAttendanceLogs: () => api.get("/api/employees/attendance/"),
  getLeaveRequests: () => api.get("/api/employees/leave-requests/"),
  createLeaveRequest: (data) => api.post("/api/employees/leave-requests/", data),
  approveLeaveRequest: (id) => api.post(`/api/employees/leave-requests/${id}/approve/`),
  rejectLeaveRequest: (id, reason) => api.post(`/api/employees/leave-requests/${id}/reject/`, { rejection_reason: reason }),
  getSalaryConfigs: () => api.get("/api/employees/salary-config/"),
  saveSalaryConfig: (data) => api.post("/api/employees/salary-config/", data),
  getPayrolls: (month) => api.get(`/api/employees/payroll/${month ? `?month=${month}` : ''}`),
  previewPayroll: (data) => api.post("/api/employees/payroll/preview/", data),
  generatePayroll: (data) => api.post("/api/employees/payroll/generate/", data),
  markPayrollPaid: (id) => api.post(`/api/employees/payroll/${id}/mark-paid/`),
  getCollections: () => api.get("/api/employees/collections/"),
  createCollection: (data) => api.post("/api/employees/collections/", data),
  verifyCollection: (id) => api.post(`/api/employees/collections/${id}/verify/`),
  rejectCollection: (id) => api.post(`/api/employees/collections/${id}/reject/`),
};


// ============ Warehouse/Stock APIs ============
export const warehouseAPI = {
  getStockLevels: () => api.get("/warehouse/stock"),
  getAllStock: () => api.get("/warehouse/stock"),
  getStockByWarehouse: (warehouseId) => api.get(`/warehouse/${warehouseId}/stock`),
  updateStock: (id, stockData) => api.put(`/warehouse/stock/${id}`, stockData),
  getLowStockAlerts: () => api.get("/warehouse/low-stock"),
  getLowStockItems: () => api.get("/warehouse/stock/low"),
  getStockMovements: (params) => api.get("/warehouse/movements", { params }),
};

// ============ Audit/Logs APIs ============
export const auditAPI = {
  getAuditLogs: () => api.get("/audit/logs"),
  getUserActivity: (userId) => api.get(`/audit/activity/${userId}`),
  getActivityLog: (userId) => api.get(`/audit/activity/${userId}`),
};

// ============ Super Admin APIs ============
export const superAdminAPI = {
  // User Management
  getAllUsers: (params = {}) => api.get("/api/super-admin/users/", { params }),
  getUser: (id) => api.get(`/api/super-admin/users/${id}/`),
  createUser: (userData) => api.post("/api/super-admin/users/", userData),
  updateUser: (id, userData) => api.put(`/api/super-admin/users/${id}/`, userData),
  deleteUser: (id) => api.delete(`/api/super-admin/users/${id}/`),

  // User Status Management
  suspendUser: (id) => api.post(`/api/super-admin/users/${id}/suspend/`),
  activateUser: (id) => api.post(`/api/super-admin/users/${id}/activate/`),
  makeSuperAdmin: (id) => api.post(`/api/super-admin/users/${id}/make_super_admin/`),
  revokeSuperAdmin: (id) => api.post(`/api/super-admin/users/${id}/revoke_super_admin/`),

  // System Settings
  getSettings: () => api.get("/api/super-admin/settings/"),
  updateSettings: (settingsData) => api.put("/api/super-admin/settings/", settingsData),
  updateGstTax: (gstTaxData) => api.post("/api/super-admin/settings/update_gst_tax/", gstTaxData),

  // Dashboard & Analytics
  getDashboardStats: () => api.get("/api/super-admin/dashboard-stats/"),
  getGlobalReports: (type) => api.get(`/api/super-admin/reports/?type=${type}`),

  // Activity Logs
  getActivityLogs: (params = {}) => api.get("/api/super-admin/logs/", { params }),

  // Units Management
  getAllUnits: () => api.get("/api/super-admin/units/"),
  getUnit: (id) => api.get(`/api/super-admin/units/${id}/`),
  createUnit: (unitData) => api.post("/api/super-admin/units/", unitData),
  updateUnit: (id, unitData) => api.put(`/api/super-admin/units/${id}/`, unitData),
  deleteUnit: (id) => api.delete(`/api/super-admin/units/${id}/`),

  // System Notifications
  getNotifications: (params = {}) => api.get("/api/super-admin/notifications/", { params }),
  markNotificationRead: (id) => api.post(`/api/super-admin/notifications/${id}/mark_read/`),
  markAllNotificationsRead: () => api.post("/api/super-admin/notifications/mark_all_read/"),
};

// ============ Support APIs ============
export const supportAPI = {
  getAllTickets: () => api.get("/api/support/tickets/"),
  getTicket: (id) => api.get(`/api/support/tickets/${id}/`),
  createTicket: (data) => api.post("/api/support/tickets/", data),
  deleteTicket: (id) => api.delete(`/api/support/tickets/${id}/`),
  replyToTicket: (id, data) => api.post(`/api/support/tickets/${id}/reply/`, data),
  updateStatus: (id, data) => api.post(`/api/support/tickets/${id}/update_status/`, data),
  escalateTicket: (id) => api.post(`/api/support/tickets/${id}/escalate/`),
  // Notifications
  getNotifications: () => api.get("/api/support/notifications/"),
  markRead: (id) => api.post(`/api/support/notifications/${id}/mark_read/`),
  markAllRead: () => api.post("/api/support/notifications/mark_all_read/"),
  markTicketRead: (ticketId) => api.post("/api/support/notifications/mark_ticket_read/", { ticket_id: ticketId }),
};

// ============ Discount APIs ============
export const discountAPI = {
  // Rules
  getAllRules: () => api.get("/api/billing/discount-rules/"),
  getRule: (id) => api.get(`/api/billing/discount-rules/${id}/`),
  createRule: (data) => api.post("/api/billing/discount-rules/", data),
  updateRule: (id, data) => api.put(`/api/billing/discount-rules/${id}/`, data),
  deleteRule: (id) => api.delete(`/api/billing/discount-rules/${id}/`),

  // Logs
  getLogs: (params = {}) => api.get("/api/billing/discount-logs/", { params }),
};

// ============ Public Subscription API ============
export const subscriptionAPI = {
  getPlans: () => api.get("/api/subscriptions/plans/"),
};

// Export default api instance as well
export const leadsAPI = {
  submitRequest: (data) => api.post("/api/leads/submit/", data),
  getAllRequests: () => authAxios.get("/api/leads/admin/list/"),
  updateRequestStatus: (id, payload) => authAxios.patch(`/api/leads/admin/${id}/`, payload),
  deleteRequest: (id) => authAxios.delete(`/api/leads/admin/${id}/`),
};

export const contactLeadAPI = {
  submit: (data) => api.post("/api/leads/contact/submit/", data),
  getAll: (params = {}) => authAxios.get("/api/leads/contact/admin/list/", { params }),
  updateStatus: (id, payload) => authAxios.patch(`/api/leads/contact/admin/${id}/`, payload),
  delete: (id) => authAxios.delete(`/api/leads/contact/admin/${id}/`),
};

// ============ FAQ APIs ============
export const faqAdminAPI = {
  getAllFAQs: () => api.get("/api/common/admin/faqs/"),
  getFAQ: (id) => api.get(`/api/common/admin/faqs/${id}/`),
  createFAQ: (data) => api.post("/api/common/admin/faqs/", data),
  updateFAQ: (id, data) => api.put(`/api/common/admin/faqs/${id}/`, data),
  deleteFAQ: (id) => api.delete(`/api/common/admin/faqs/${id}/`),
};

export const faqPublicAPI = {
  getFAQs: () => api.get("/api/common/faqs/"),
};

// ============ Dynamic Content APIs ============
export const contentAdminAPI = {
  getAllPages: () => api.get("/api/common/admin/content/"),
  getPage: (slug) => api.get(`/api/common/admin/content/${slug}/`),
  createPage: (data) => api.post("/api/common/admin/content/", data),
  updatePage: (slug, data) => api.put(`/api/common/admin/content/${slug}/`, data),
  deletePage: (slug) => api.delete(`/api/common/admin/content/${slug}/`),
};

export const contentPublicAPI = {
  getPage: (slug) => api.get(`/api/common/content/${slug}/`),
};

// ============ Business Type APIs ============
export const businessTypeAdminAPI = {
  getAll: () => api.get("/api/common/admin/business-types/"),
  getOne: (id) => api.get(`/api/common/admin/business-types/${id}/`),
  create: (data) => api.post("/api/common/admin/business-types/", data),
  update: (id, data) => api.put(`/api/common/admin/business-types/${id}/`, data),
  delete: (id) => api.delete(`/api/common/admin/business-types/${id}/`),
};

export const businessTypePublicAPI = {
  getAll: () => api.get("/api/common/business-types/"),
};

export const whatsappAPI = {
  sendInvoice: (invoiceId) => api.post("/api/send-whatsapp-invoice", { invoiceId }),
};

export default api;
